import { Progress } from "@/components/ui/progress";
import { motion } from "framer-motion";
import { Sprout, CheckCircle2, Sparkles, ChevronRight, Link } from "lucide-react";
import { IntegrationSelection } from "../IntegrationSelecting";
import LightPreviewNotice from "../LightPreviewNotice";
import { useOnboarding } from "@/context/OnboardingContext";
import { steps } from "@/data/onboarding.data";
import useUserInteractionHandling from "@/hooks/useUserInteractionHandling";

export default function WebsiteOnboardingStep() {
  const { state, expandedSteps, stepTexts, selectedIntegrations } = useOnboarding();
  const { progress, currentStepIndex, currentStepText } = state;

  const { handleToggleStepExpanded, handleIntegrationToggle } =
    useUserInteractionHandling();

  return (
    <motion.div
      key="step-1"
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -20 }}
      transition={{
        duration: 0.5,
        ease: "easeInOut",
        opacity: { duration: 0.3 },
      }}
      className="w-full py-4"
    >
      <div className="w-full mx-auto max-w-5xl">
        <div className="space-y-4 mb-8">
          <div className="flex items-center gap-2">
            <motion.div
              initial={{ scale: 0, rotate: -180 }}
              animate={{ scale: 1, rotate: 0 }}
              transition={{
                type: "spring",
                stiffness: 260,
                damping: 20,
                delay: 0.2,
              }}
            >
              <Sprout className="w-6 h-6 text-primary" />
            </motion.div>
            <motion.h2
              className="text-2xl font-semibold"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.3 }}
            >
              Let's grow your content garden
            </motion.h2>
          </div>
          <p className="text-muted-foreground">
            First, let's connect your website to start growing your content.
          </p>
          <LightPreviewNotice className="mt-1" />
        </div>

        <div className="mb-8">
          <Progress value={progress} className="mb-2" />
          <div className="flex justify-between items-center">
            <p className="text-sm text-muted-foreground">
              Step {currentStepIndex + 1} of {steps.length}
            </p>
            <p className="text-sm font-medium">{Math.round(progress)}%</p>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Left side - Analysis Steps */}
          <div className="bg-muted/50 p-4 rounded-md border max-h-[500px] overflow-y-auto">
            <div className="space-y-2">
              {steps
                .slice(0, currentStepIndex + 1)
                .reverse()
                .map((step, index) => {
                  const isCurrentStep = index === 0;
                  const isExpanded = isCurrentStep || expandedSteps[step.id];
                  const stepText = isCurrentStep
                    ? currentStepText
                    : stepTexts[step.id] || "";

                  return (
                    <motion.div
                      key={`${step.id}-${index}`}
                      initial={index === 0 ? { opacity: 0, y: -20, scale: 0.95 } : false}
                      animate={index === 0 ? { opacity: 1, y: 0, scale: 1 } : false}
                      transition={{
                        duration: 0.4,
                        ease: "easeOut",
                      }}
                      layout="position"
                      className={`flex items-start gap-3 p-3 rounded-lg transition-all duration-300 ${
                        isCurrentStep
                          ? "bg-primary/5 border border-primary/20 shadow-sm"
                          : "bg-background/50 hover:bg-background/80"
                      }`}
                    >
                      <div className="mt-1 flex-shrink-0">
                        {!isCurrentStep ? (
                          <motion.div
                            initial={{ scale: 0 }}
                            animate={{ scale: 1 }}
                            transition={{ type: "spring", stiffness: 300, damping: 20 }}
                          >
                            <CheckCircle2 className="w-5 h-5 text-primary" />
                          </motion.div>
                        ) : (
                          <motion.div
                            animate={{
                              scale: [1, 1.1, 1],
                              rotate: [0, 5, -5, 0],
                            }}
                            transition={{
                              duration: 2,
                              repeat: Infinity,
                              ease: "easeInOut",
                            }}
                          >
                            <Sparkles className="w-5 h-5 text-primary" />
                          </motion.div>
                        )}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div
                          className="flex items-center justify-between cursor-pointer group"
                          onClick={() =>
                            !isCurrentStep && handleToggleStepExpanded(step.id)
                          }
                        >
                          <div className="min-w-0">
                            <motion.p
                              className="font-medium truncate"
                              initial={index === 0 ? { opacity: 0, x: -10 } : false}
                              animate={index === 0 ? { opacity: 1, x: 0 } : false}
                              transition={{ duration: 0.3 }}
                            >
                              {step.name}
                            </motion.p>
                          </div>
                          {!isCurrentStep && (
                            <motion.div
                              whileHover={{ scale: 1.1 }}
                              whileTap={{ scale: 0.95 }}
                              className="flex-shrink-0 ml-2"
                            >
                              <ChevronRight
                                className={`w-4 h-4 text-muted-foreground transition-transform duration-300 ${
                                  isExpanded ? "rotate-90" : ""
                                }`}
                              />
                            </motion.div>
                          )}
                        </div>
                        {isCurrentStep && currentStepText && (
                          <motion.div
                            initial={{ opacity: 0, height: 0 }}
                            animate={{ opacity: 1, height: "auto" }}
                            transition={{ duration: 0.4, ease: "easeOut" }}
                            className="mt-2 text-sm"
                          >
                            {currentStepText.split("\n").map((line, i) => {
                              if (line.startsWith("/") || line.startsWith("http")) {
                                return (
                                  <motion.div
                                    key={i}
                                    initial={{ opacity: 0, x: -10 }}
                                    animate={{ opacity: 1, x: 0 }}
                                    transition={{ duration: 0.3, delay: i * 0.1 }}
                                    className="flex items-center gap-2 text-primary/80 hover:text-primary transition-colors"
                                  >
                                    <Link className="w-4 h-4 flex-shrink-0" />
                                    <span className="truncate">{line}</span>
                                  </motion.div>
                                );
                              }
                              return (
                                <motion.p
                                  key={i}
                                  initial={{ opacity: 0, y: 10 }}
                                  animate={{ opacity: 1, y: 0 }}
                                  transition={{ duration: 0.3, delay: i * 0.1 }}
                                  className={`${i > 0 ? "mt-1" : ""} break-words`}
                                >
                                  {line}
                                </motion.p>
                              );
                            })}
                          </motion.div>
                        )}
                        {!isCurrentStep && isExpanded && stepText && (
                          <motion.div
                            initial={{ opacity: 0, height: 0 }}
                            animate={{ opacity: 1, height: "auto" }}
                            exit={{ opacity: 0, height: 0 }}
                            transition={{ duration: 0.3 }}
                            className="mt-2 text-sm"
                          >
                            {stepText.split("\n").map((line, i) => (
                              <motion.p
                                key={i}
                                initial={{ opacity: 0, y: 10 }}
                                animate={{ opacity: 1, y: 0 }}
                                transition={{ duration: 0.3, delay: i * 0.1 }}
                                className={`${i > 0 ? "mt-1" : ""} break-words`}
                              >
                                {line}
                              </motion.p>
                            ))}
                          </motion.div>
                        )}
                      </div>
                    </motion.div>
                  );
                })}
            </div>
          </div>

          {/* Right side - Integration Selection */}
          <IntegrationSelection
            selectedIntegrations={selectedIntegrations}
            onIntegrationToggle={handleIntegrationToggle}
          />
        </div>
      </div>
    </motion.div>
  );
}
